import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ErrorBoundary, useToast } from '@/components/common';
import { useSessionListStore } from '@/stores/useSessionListStore';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFirmContextStore } from '@/stores/useFirmContextStore';
import { useSessionsList, useDeleteSession } from '@/hooks/useSession';
import { exportSession } from '@/api/endpoints';
import {
  SessionList,
  SessionFilterBar,
  WelcomeBanner,
  ActiveWorkSection,
  GetStartedCard,
  useFilteredSessions,
  type SessionSortOption,
  type DateRange,
  type ExportFormat,
} from '@/features/sessions';
import type { DdqSession, SessionPriority, SessionSummary } from '@/types/api.types';

type SessionStatus = DdqSession['status'];

/**
 * How long a session that exists only in the local store (not yet returned by
 * the list API) is retained before the API is treated as authoritative. Covers
 * eventual consistency right after creation without letting stale ghosts persist.
 */
const LOCAL_ONLY_RETENTION_MS = 60_000;

/**
 * Sessions page wrapped with error boundary for graceful error handling.
 */
export function SessionsPage() {
  const handleError = useCallback((error: Error) => {
    console.error('[SessionsPage] Error caught by boundary:', error);
  }, []);

  return (
    <ErrorBoundary onError={handleError}>
      <SessionsPageContent />
    </ErrorBoundary>
  );
}

/**
 * Internal sessions page content component.
 */
/**
 * Map API SessionSummary to frontend DdqSession format.
 */
function mapSessionSummary(s: SessionSummary): DdqSession {
  const statusMap: Record<string, DdqSession['status']> = {
    pending: 'Pending',
    processing: 'Processing',
    ready: 'Ready',
    failed: 'Error',
  };

  return {
    id: s.session_id,
    fileName: s.uploaded_filename,
    totalQuestions: s.question_count,
    processedQuestions: 0, // Not available in summary — hydrated when session detail is loaded
    exactMatches: 0,
    suggestedMatches: 0,
    generatedResponses: 0,
    noMatches: 0,
    automationRate: s.automation_rate ?? 0,
    status: statusMap[s.status] ?? 'Pending',
    created: s.created_at,
    priority: s.priority,
    sourceConsultant: s.source_consultant,
    accountName: s.account_name,
  };
}

function SessionsPageContent() {
  const navigate = useNavigate();
  const { success: showSuccess, info: showInfo, error: showError } = useToast();
  const sessions = useSessionListStore((state) => state.sessions);
  const setSessions = useSessionListStore((state) => state.setSessions);
  const removeSession = useSessionListStore((state) => state.removeSession);
  const { user } = useAuthStore();
  const activeFirmId = useFirmContextStore((state) => state.activeFirmId);

  // Fetch sessions from API and hydrate store
  const { data: apiResponse, isLoading: isLoadingApi } = useSessionsList({
    firm_id: activeFirmId ?? undefined,
  });
  const deleteSessionMutation = useDeleteSession();

  useEffect(() => {
    if (apiResponse?.sessions) {
      const apiSessions = apiResponse.sessions.map(mapSessionSummary);

      // Merge: the API is the source of truth. We preserve a local-only session
      // ONLY if it was created within LOCAL_ONLY_RETENTION_MS — the eventual-
      // consistency window where a just-created session (POST /sessions) may not
      // yet appear in the list response. Older local-only entries are dropped, so
      // stale "ghost" sessions (e.g. cached in localStorage while the API was
      // unavailable) no longer linger forever and 404 when opened.
      const apiIds = new Set(apiSessions.map((s) => s.id));
      const now = Date.now();
      const localOnly = sessions.filter((s) => {
        if (apiIds.has(s.id)) return false;
        const createdMs = new Date(s.created).getTime();
        return Number.isFinite(createdMs) && now - createdMs < LOCAL_ONLY_RETENTION_MS;
      });
      setSessions([...localOnly, ...apiSessions]);
    }
  // Only re-run when API data changes, not when sessions change (avoids infinite loop)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponse, setSessions]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<SessionStatus[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<SessionPriority[]>([]);
  const [sortBy, setSortBy] = useState<SessionSortOption>('priority');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [dateRange, setDateRange] = useState<DateRange>({ from: null, to: null });

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);

  // Computed filtered sessions
  const filteredSessions = useFilteredSessions(sessions, {
    searchQuery,
    selectedStatuses,
    selectedPriorities,
    sortBy,
    dateRange,
  });

  // Handlers
  const handleStatusToggle = useCallback((status: SessionStatus) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  }, []);

  const handlePriorityToggle = useCallback((priority: SessionPriority) => {
    setSelectedPriorities((prev) =>
      prev.includes(priority)
        ? prev.filter((p) => p !== priority)
        : [...prev, priority]
    );
  }, []);

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      // Processing/pending sessions are clickable too - the review page
      // shows live progress and takes over when processing completes.
      navigate(`/sessions/${sessionId}/review`);
    },
    [navigate]
  );

  const handleReviewSession = useCallback(
    (sessionId: string) => {
      navigate(`/sessions/${sessionId}/review`);
    },
    [navigate]
  );

  const handleExportSession = useCallback(async (sessionId: string, format: ExportFormat) => {
    showInfo('Export Started', `Exporting session as ${format.toUpperCase()}...`);

    try {
      const blob = await exportSession(sessionId, format);

      // Get filename from session if available
      const session = sessions.find((s) => s.id === sessionId);
      const baseName = session?.fileName?.replace(/\.[^/.]+$/, '') || `ddq-session-${sessionId}`;
      const filename = `${baseName}-export.${format}`;

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('Export Complete', `Session exported as ${filename}`);
    } catch (error) {
      console.error('Export failed:', error);
      showError('Export Failed', 'Unable to export session. Please try again.');
    }
  }, [showInfo, showSuccess, showError, sessions]);

  const handleDeleteSession = useCallback((sessionId: string) => {
    setSessionToDelete(sessionId);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDeleteSession = useCallback(() => {
    if (sessionToDelete) {
      // Remove from local store immediately (optimistic)
      removeSession(sessionToDelete);
      // Also delete via API
      deleteSessionMutation.mutate(sessionToDelete, {
        onError: () => {
          showError('Delete Failed', 'Unable to delete session from server.');
        },
      });
      showSuccess('Session Deleted', 'The session has been removed.');
      setSessionToDelete(null);
      setDeleteDialogOpen(false);
    }
  }, [sessionToDelete, removeSession, deleteSessionMutation, showSuccess, showError]);

  const cancelDeleteSession = useCallback(() => {
    setSessionToDelete(null);
    setDeleteDialogOpen(false);
  }, []);

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <WelcomeBanner
        userName={user?.userName}
        hasExistingSessions={sessions.length > 0}
      />

      {isLoadingApi && sessions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading your sessions...</p>
          </CardContent>
        </Card>
      ) : sessions.length === 0 ? (
        /* F6: self-serve opt-in entry with instant vault readiness */
        <GetStartedCard />
      ) : (
        <>
          {/* Active Work - Sessions needing review */}
          <ActiveWorkSection sessions={sessions} />

          {/* Page Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Your Questionnaires</h1>
              <p className="text-sm text-muted-foreground">
                Upload, review, and export DDQ/RFP responses
              </p>
            </div>
            <Button asChild>
              <Link to="/upload">
                <Upload className="h-4 w-4 mr-2" />
                Upload Questionnaire
              </Link>
            </Button>
          </div>

          {/* Filter Bar */}
          <SessionFilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedStatuses={selectedStatuses}
            onStatusToggle={handleStatusToggle}
            selectedPriorities={selectedPriorities}
            onPriorityToggle={handlePriorityToggle}
            sortBy={sortBy}
            onSortChange={setSortBy}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            resultCount={filteredSessions.length}
          />

          {/* Sessions List */}
          {filteredSessions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm font-medium mb-1">No results match your filters</p>
                <p className="text-sm text-muted-foreground">
                  Try adjusting your search or filter criteria.
                </p>
              </CardContent>
            </Card>
          ) : (
            <SessionList
              sessions={filteredSessions}
              viewMode={viewMode}
              onSelectSession={handleSelectSession}
              onReviewSession={handleReviewSession}
              onExportSession={handleExportSession}
              onDeleteSession={handleDeleteSession}
            />
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              session and all associated responses.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteSession}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteSession}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
